/**
 * Embed Widget (Story 5.26, Task 2)
 *
 * Renders external content via sandboxed iframe. Four states:
 * - Empty: no URL configured — shows placeholder
 * - Edit mode: static provider card (no live iframe for performance)
 * - Loading: skeleton while iframe loads
 * - Live: sandboxed iframe in preview mode
 *
 * Security: sandbox attribute, HTTPS-only, no allow-top-navigation.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Code, LinkSimple, ArrowSquareOut } from "@phosphor-icons/react";
import type { WidgetConfig } from "../../../lib/templateUtils";

interface EmbedWidgetProps {
  widget: WidgetConfig;
  isPreviewMode?: boolean;
}

type AspectRatio = "16:9" | "4:3" | "1:1";

const ASPECT_RATIO_VALUES: Record<AspectRatio, string> = {
  "16:9": "16/9",
  "4:3": "4/3",
  "1:1": "1/1",
};

/** Iframe sandbox permissions — no allow-top-navigation, no allow-forms */
const SANDBOX_PERMISSIONS =
  "allow-scripts allow-same-origin allow-popups allow-presentation";

/** Iframe allow attribute for media players */
const IFRAME_ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";

/** Timeout for iframe load (ms) */
const LOAD_TIMEOUT_MS = 10_000;

export function EmbedWidget({ widget, isPreviewMode = false }: EmbedWidgetProps) {
  const config = widget.config as {
    url?: string;
    embedUrl?: string;
    provider?: string;
    title?: string;
    aspectRatio?: AspectRatio;
    showBorder?: boolean;
  };

  const url = config.url ?? "";
  const embedUrl = config.embedUrl ?? "";
  const provider = config.provider ?? "Unknown";
  const title = config.title ?? "";
  const aspectRatio = config.aspectRatio ?? "16:9";
  const showBorder = config.showBorder ?? true;

  // Determine if iframe should render on mount (preview mode with URL)
  const shouldIframeOnMount = isPreviewMode && !!embedUrl;

  const [isLoading, setIsLoading] = useState(shouldIframeOnMount);
  const [hasError, setHasError] = useState(false);
  const [clickedPreview, setClickedPreview] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start timeout when loading starts in preview mode
  useEffect(() => {
    if (shouldIframeOnMount) {
      timeoutRef.current = setTimeout(() => {
        setIsLoading(false);
        setHasError(true);
      }, LOAD_TIMEOUT_MS);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [shouldIframeOnMount]);

  // Reset state when URL changes
  useEffect(() => {
    setHasError(false);
    setIsLoading(!!embedUrl && isPreviewMode);
    setClickedPreview(false);
  }, [embedUrl, isPreviewMode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleIframeLoad = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsLoading(false);
  }, []);

  const handleIframeError = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsLoading(false);
    setHasError(true);
  }, []);

  const startLoadingWithTimeout = useCallback(() => {
    setIsLoading(true);
    setHasError(false);
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
    }, LOAD_TIMEOUT_MS);
  }, []);

  const borderClass = showBorder ? "border border-border" : "";

  // ── Empty state: no URL configured ──
  if (!url) {
    return (
      <div
        data-testid="widget-type-embed"
        className="flex h-full flex-col items-center justify-center gap-2 p-6 text-muted-foreground"
      >
        <LinkSimple size={32} className="opacity-50" />
        <span className="text-sm" data-testid="embed-placeholder">
          Paste a URL to embed external content
        </span>
      </div>
    );
  }

  // ── Error state ──
  if (hasError) {
    return (
      <div
        data-testid="widget-type-embed"
        className={`flex h-full flex-col items-center justify-center gap-2 rounded-lg p-6 text-muted-foreground ${borderClass}`}
      >
        <Code size={32} className="opacity-30" />
        <span className="text-sm">Content could not be loaded</span>
        <button
          data-testid="embed-retry-button"
          className="text-xs font-medium text-primary hover:underline"
          aria-label="Retry loading embedded content"
          onClick={() => {
            startLoadingWithTimeout();
            setClickedPreview(true);
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Edit mode: static placeholder card ──
  const shouldShowStaticPlaceholder = !isPreviewMode && !clickedPreview;
  if (shouldShowStaticPlaceholder) {
    return (
      <div
        data-testid="widget-type-embed"
        className={`flex h-full flex-col overflow-hidden rounded-lg ${borderClass}`}
      >
        {title && (
          <div className="truncate px-4 pt-3 text-sm font-semibold" data-testid="embed-title">
            {title}
          </div>
        )}
        <div
          data-testid="embed-edit-placeholder"
          className="flex flex-1 flex-col items-center justify-center gap-3 bg-muted/30 p-6"
          style={{ aspectRatio: ASPECT_RATIO_VALUES[aspectRatio] }}
        >
          <Code size={24} className="text-muted-foreground" />
          <div
            data-testid="embed-provider-badge"
            className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
          >
            {provider}
          </div>
          <button
            data-testid="embed-preview-button"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => {
              startLoadingWithTimeout();
              setClickedPreview(true);
            }}
          >
            Click to preview
          </button>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
              data-testid="embed-open-link"
            >
              <ArrowSquareOut size={12} /> Open in new tab
            </a>
          )}
        </div>
      </div>
    );
  }

  // ── Live/Loading state: sandboxed iframe ──
  return (
    <div
      data-testid="widget-type-embed"
      className={`flex h-full flex-col overflow-hidden rounded-lg ${borderClass}`}
    >
      {title && (
        <div className="truncate px-4 pt-3 text-sm font-semibold" data-testid="embed-title">
          {title}
        </div>
      )}
      <div className="flex-1 relative" style={{ aspectRatio: ASPECT_RATIO_VALUES[aspectRatio] }}>
        {isLoading && (
          <div
            data-testid="embed-loading"
            className="absolute inset-0 bg-muted animate-pulse rounded"
          />
        )}
        <iframe
          data-testid="embed-iframe"
          src={embedUrl}
          sandbox={SANDBOX_PERMISSIONS}
          referrerPolicy="no-referrer"
          loading="lazy"
          allow={IFRAME_ALLOW}
          title={title || `Embedded content from ${provider}`}
          className={`w-full h-full ${isLoading ? "opacity-0" : "opacity-100"}`}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>
    </div>
  );
}
